import { useCallback } from 'react'
import useGtmEvent from './useGtmEvent'
import { VisaItineraryData, SearchCriteria, TravellerDetails } from '@/src/types/visa/visaItinerary'

interface VisaGtmEventHook {
    trackVisaHomePageView: () => void
    trackVisaItineraryPageView: (itineraryData?: VisaItineraryData) => void
    trackVisaPurchasePageView: (itineraryId: string, tripId: string) => void
    trackVisaDetailsContinueClick: (formData: {
        nationality?: string
        destination?: string
        visaType?: string
        processingSpeed?: string
        dateOfVisa?: string
        adults?: number
        children?: number
    }) => void
    trackVisaReviewButtonClick: (leadTraveller?: TravellerDetails) => void
    trackCouponApplyClick: (couponCode?: string, discountAmount?: number) => void
    trackPaymentDetailsButtonClick: () => void
}

const useVisaGtmEvent = (): VisaGtmEventHook => {
    const { pushEventToDataLayer } = useGtmEvent()

    // Page view event for Visa Home Page
    const trackVisaHomePageView = useCallback(() => {
        pushEventToDataLayer({
            event: 'page_view_visa_home',
            page_type: 'visa_home',
            page_title: 'Visa Home',
            page_url: window.location.href,
            page_path: window.location.pathname,
            timestamp: new Date().toISOString()
        })
    }, [pushEventToDataLayer])

    // Page view event for Visa Itinerary Page
    const trackVisaItineraryPageView = useCallback(
        (itineraryData?: VisaItineraryData) => {
            const payload: Record<string, any> = {
                event: 'page_view_visa_itinerary',
                page_type: 'visa_itinerary',
                page_title: 'Visa Itinerary',
                page_url: window.location.href,
                page_path: window.location.pathname,
                timestamp: new Date().toISOString()
            }

            // Add major itinerary data if available
            if (itineraryData) {
                const { searchCriteria, itineraryId, totalFareComponent, appliedCoupon } = itineraryData

                payload.itinerary_id = itineraryId
                payload.destination_country = searchCriteria?.destinationCountry
                payload.nationality = searchCriteria?.nationality
                payload.visa_type = searchCriteria?.visaType
                payload.visa_category = searchCriteria?.visaCategory
                payload.processing_speed = searchCriteria?.processingSpeed
                payload.date_of_visa = searchCriteria?.dateOfVisa
                payload.total_travellers =
                    (searchCriteria?.paxInfo?.adults || 0) +
                    (searchCriteria?.paxInfo?.children || 0)
                payload.adults = searchCriteria?.paxInfo?.adults
                payload.children = searchCriteria?.paxInfo?.children
                payload.total_amount = totalFareComponent?.totalAmount
                payload.currency = totalFareComponent?.currencyCode

                // Only add coupon data if a coupon is actually applied (don't send false)
                if (appliedCoupon && appliedCoupon.couponCode) {
                    payload.coupon_applied = true
                    payload.coupon_code = appliedCoupon.couponCode
                    payload.discount_amount = appliedCoupon.discountAmount
                }
                // Don't send coupon_applied: false - only send when actually applied
            }

            pushEventToDataLayer(payload)
        },
        [pushEventToDataLayer]
    )

    // Page view event for Visa Purchase/Confirmation Page
    const trackVisaPurchasePageView = useCallback(
        (itineraryId: string, tripId: string) => {
            pushEventToDataLayer({
                event: 'page_view_visa_purchase',
                page_type: 'visa_purchase',
                page_title: 'Visa Purchase Confirmation',
                page_url: window.location.href,
                page_path: window.location.pathname,
                itinerary_id: itineraryId,
                trip_id: tripId,
                timestamp: new Date().toISOString()
            })
        },
        [pushEventToDataLayer]
    )

    // Continue button click on Visa Details step (Step 1)
    const trackVisaDetailsContinueClick = useCallback(
        (formData: {
            nationality?: string
            destination?: string
            visaType?: string
            processingSpeed?: string
            dateOfVisa?: string
            adults?: number
            children?: number
        }) => {
            pushEventToDataLayer({
                event: 'click_visa_details_continue_button',
                action: 'click',
                category: 'visa_details',
                label: 'continue_button',
                nationality: formData.nationality,
                destination: formData.destination,
                visa_type: formData.visaType,
                processing_speed: formData.processingSpeed,
                date_of_visa: formData.dateOfVisa,
                adults: formData.adults,
                children: formData.children,
                total_travellers: (formData.adults || 0) + (formData.children || 0),
                timestamp: new Date().toISOString()
            })
        },
        [pushEventToDataLayer]
    )

    // Proceed to Review button click (Step 2 - after filling traveller info)
    const trackVisaReviewButtonClick = useCallback(
        (leadTraveller?: TravellerDetails) => {
            const payload: Record<string, any> = {
                event: 'click_visa_review_button',
                action: 'click',
                category: 'visa_traveller_info',
                label: 'proceed_to_review',
                timestamp: new Date().toISOString()
            }

            // Add flattened lead traveller info (first/primary traveller only)
            if (leadTraveller) {
                const { personalDetails, passportDetails } = leadTraveller
                payload.lead_traveller_first_name = personalDetails?.firstName
                payload.lead_traveller_last_name = personalDetails?.lastName
                payload.lead_traveller_gender = personalDetails?.gender
                payload.lead_traveller_pax_type = personalDetails?.paxType
                payload.lead_traveller_date_of_birth = personalDetails?.dateOfBirth
                payload.lead_traveller_passport_expiry = passportDetails?.expiryDate
            }

            pushEventToDataLayer(payload)
        },
        [pushEventToDataLayer]
    )

    // Coupon Apply button click on Itinerary page
    const trackCouponApplyClick = useCallback((couponCode?: string, discountAmount?: number) => {
        pushEventToDataLayer({
            event: 'click_visa_coupon_apply_button',
            action: 'click',
            category: 'visa_itinerary',
            label: 'coupon_apply',
            coupon_code: couponCode || null,
            discount_amount: discountAmount || null,
            timestamp: new Date().toISOString()
        })
    }, [pushEventToDataLayer])

    // Continue to Payment button click on Review page
    const trackPaymentDetailsButtonClick = useCallback(() => {
        pushEventToDataLayer({
            event: 'click_visa_payment_details_button',
            action: 'click',
            category: 'visa_review',
            label: 'continue_to_payment',
            timestamp: new Date().toISOString()
        })
    }, [pushEventToDataLayer])

    return {
        trackVisaHomePageView,
        trackVisaItineraryPageView,
        trackVisaPurchasePageView,
        trackVisaDetailsContinueClick,
        trackVisaReviewButtonClick,
        trackCouponApplyClick,
        trackPaymentDetailsButtonClick
    }
}

export default useVisaGtmEvent
